/** One page of `/wallet/networks`, or the error that came back. */
export type PageResult = { rows?: unknown[]; error?: unknown };

/**
 * Far above the 87 networks the catalogue holds, so today one request covers
 * it and the walk below never takes a second lap.
 */
export const CATALOGUE_PAGE_SIZE = 2000;
/** Enough for a catalogue many times the size of the real one. */
export const CATALOGUE_MAX_PAGES = 25;

/**
 * Reads every page of the network catalogue.
 *
 * Measured against dev rather than read off the Swagger, which describes
 * `page` as a limit and `pageLimit` as an offset and is no help: the endpoint
 * paginates properly, honours `pageLimit` exactly, and counts `page` **from
 * zero** — with `pageLimit=3`, page 0 returns the first three networks and
 * page 1 the next three. Starting the walk at 1 would have skipped the first
 * page outright.
 *
 * So the walk starts at 0 and ends at the first page shorter than the size it
 * asked for. Two smaller guards survive from before that measurement, because
 * they cost nothing and a paging contract can change under us: an empty first
 * page does not end the walk, and neither does a page that only repeats rows
 * already read.
 */
/**
 * Identity of a network row, for spotting a page that repeats what has
 * already been read. `symbol` backs `id` up because it is the value the rest
 * of the feature keys on and the one field a network cannot be missing.
 */
const rowKey = (row: unknown): string | null => {
  const id = (row as any)?.id;
  if (id !== undefined && id !== null) return `id:${id}`;
  const symbol = (row as any)?.symbol;
  return symbol ? `symbol:${symbol}` : null;
};

export const collectCataloguePages = async (
  fetchPage: (page: number) => Promise<PageResult>
): Promise<{ rows: unknown[]; error?: unknown }> => {
  const rowsById = new Map<string, unknown>();
  let sawRows = false;

  for (let page = 0; page < CATALOGUE_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await fetchPage(page);

    if (result.error) {
      // Fail the whole read rather than return part of a catalogue: a
      // half-loaded network list is indistinguishable from a complete one.
      return { rows: [], error: result.error };
    }

    const rows = Array.isArray(result.rows) ? result.rows : [];
    const sizeBefore = rowsById.size;

    rows.forEach((row) => {
      const key = rowKey(row);
      if (key !== null) rowsById.set(key, row);
    });

    const addedRows = rowsById.size > sizeBefore;
    if (addedRows) {
      sawRows = true;
    } else if (sawRows) {
      // An empty or wholly-duplicate page ends the walk once something has
      // been read — which is what an endpoint that ignores `page` produces.
      break;
    } else if (page >= 1) {
      // Nothing at page 0 or page 1 means nothing under either convention,
      // so the catalogue really is empty — do not walk to the page cap.
      break;
    }

    // A page smaller than the one asked for is the last page, checked only
    // once real rows have been read so an empty page cannot end the walk
    // before it starts. The endpoint honours `pageLimit`, so a short page
    // really is the end rather than a server capping the request.
    if (sawRows && rows.length < CATALOGUE_PAGE_SIZE) break;
  }

  return { rows: Array.from(rowsById.values()) };
};
